import React from "react";
import { getDownloadLink } from "@utils/url";

interface RewardItem {
  value: string;
  title: string;
  subtitle: string;
}

interface HeroRewardsBonusProps {
  title: string;
  description: string;
  rewards: RewardItem[];
  buttonText: string;
  subtitleText: string;
  imageSrc: string;
  imageAlt: string;
  appleButtonSrc: string;
  googleButtonSrc: string;
  disclaimerText?: string;
}

/**
 * Hero Rewards Bonus component
 *
 * Hero section component that displays title, description, rewards card, call-to-action button,
 * and app download buttons. Designed to highlight activation rewards and monthly bonuses.
 * Receives all content as props for A/B testing flexibility.
 *
 * @param {string} title - Main headline text
 * @param {string} description - Description text below the title (can include HTML for bold text)
 * @param {RewardItem[]} rewards - Array of reward items with value, title, and subtitle
 * @param {string} buttonText - Text for the call-to-action button
 * @param {string} subtitleText - Subtitle text below the CTA button
 * @param {string} imageSrc - Path to the hero image
 * @param {string} imageAlt - Alt text for the hero image
 * @param {string} appleButtonSrc - Path to the App Store button image
 * @param {string} googleButtonSrc - Path to the Google Play button image
 * @param {string} [disclaimerText] - Optional small print shown below the CTA subtitle
 * @returns {JSX.Element} The hero rewards bonus section component
 */
export function HeroRewardsBonus({
  title,
  description,
  rewards,
  buttonText,
  subtitleText,
  imageSrc,
  imageAlt,
  appleButtonSrc,
  googleButtonSrc,
  disclaimerText,
}: HeroRewardsBonusProps) {
  const primaryCtaHref = getDownloadLink();

  return (
    <section className="w-full max-w-6xl flex flex-col lg:flex-row items-stretch justify-between pt-20 pb-0 mb-0 px-10">
      <div className="max-w-lg lg:max-w-none lg:basis-3/5 lg:pr-12">
        <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-800">
          {title}
        </h1>
        <p
          className="mt-6 text-slate-600 text-lg"
          dangerouslySetInnerHTML={{ __html: description }}
        />

        {/* Reward Card */}
        <div className="bg-white rounded-2xl p-8 shadow-[0_10px_30px_rgba(139,99,241,0.1)] border border-[#e0e0ff] my-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-center">
            {rewards.map((reward, index) => (
              <div
                key={index}
                className={`${
                  index < rewards.length - 1
                    ? "md:border-r md:border-[#eeeeee]"
                    : ""
                }`}
              >
                <h3 className="text-[32px] text-[#8B63F1] font-extrabold">
                  {reward.value}
                </h3>
                <p className="text-sm font-semibold text-slate-800">
                  {reward.title}
                  <br />
                  <span className="font-normal opacity-70">
                    {reward.subtitle}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center my-10">
          <a
            href={primaryCtaHref}
            className="bg-[#8B63F1] text-white px-11 py-5 rounded-[50px] text-xl font-extrabold inline-block shadow-[0_10px_25px_rgba(139,99,241,0.4)] transition-all duration-300 hover:shadow-[0_15px_30px_rgba(139,99,241,0.5)] hover:scale-105"
          >
            {buttonText}
          </a>
          <p className="mt-4 text-sm text-[#636E72] font-medium">
            {subtitleText}
          </p>
          {disclaimerText && (
            <p className="mt-2 text-xs text-[#636E72] opacity-80">
              {disclaimerText}
            </p>
          )}
        </div>

        {/* App Store Badges */}
        <div className="flex gap-4 mt-8 opacity-80">
          <a
            href="https://apple.co/48hqxkB"
            target="_blank"
            rel="noopener noreferrer"
            className="block h-[35px]"
          >
            <img
              src={appleButtonSrc}
              alt="App Store"
              className="h-full w-auto"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.walrus.member"
            target="_blank"
            rel="noopener noreferrer"
            className="block h-[35px]"
          >
            <img
              src={googleButtonSrc}
              alt="Google Play"
              className="h-full w-auto"
            />
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-0 flex justify-center lg:justify-end mx-auto lg:mx-0 lg:basis-1/3 lg:min-w-[20%] lg:flex-shrink-0">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full max-w-xs lg:max-w-md object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.1)]"
        />
      </div>
    </section>
  );
}
